import { useRef, useState } from 'react';

import Button from 'components/Button';
import Text from 'components/Text';
import { usePriceContext } from 'contexts';
import { type FieldKey, useId, useValueChangeEffect } from 'hooks';
import type { CampaignResponse } from 'models/product';
import type { TemplateString } from 'types';
import { goToElement, selectTabbable } from 'utils/dom';
import { is } from 'utils/helpers';
import { useI18n } from 'utils/i18n';

import Campaign from './Campaign';
import type { AddCampaignData } from './CampaignBuyAllProducts';

interface Props {
	campaigns: CampaignResponse[] | undefined;
	campaignValidToText: FieldKey | TemplateString;
	className?: string;
	heading: string;
	onCampaignPackageAddToCartClick:
		| ((campaign: AddCampaignData) => Promise<void>)
		| undefined;
	onCampaignPackageAddToWishlistClick:
		| ((campaign: AddCampaignData) => Promise<void>)
		| undefined;
	packagePriceLabel: string;
}

const initialVisibleItemsCount = 1;

export default function ProductDetailsCampaigns({
	campaigns,
	campaignValidToText,
	className,
	heading,
	onCampaignPackageAddToCartClick,
	onCampaignPackageAddToWishlistClick,
	packagePriceLabel,
}: Props) {
	const { usePriceExcVat } = usePriceContext();

	//TODO: Expand/collapse should be some generic component.
	const { t } = useI18n();
	const itemsId = useId('campaign-list');
	const itemsContainerRef = useRef<HTMLUListElement>(null);
	const isCollapsable = Boolean(
		initialVisibleItemsCount &&
		initialVisibleItemsCount < (campaigns?.length ?? 0),
	);
	const [isExpanded, setIsExpanded] = useState<boolean>(!isCollapsable);
	const visibleItems = isExpanded
		? campaigns
		: campaigns?.slice(0, initialVisibleItemsCount);

	// Focus the first 'new' item when the list is expanded.
	useValueChangeEffect(isExpanded, () => {
		if (!initialVisibleItemsCount) {
			return;
		}
		const firstNewItem =
			itemsContainerRef.current?.children[initialVisibleItemsCount];
		if (isExpanded && is.instance(firstNewItem, HTMLElement)) {
			const tabbables = selectTabbable(firstNewItem);
			if (tabbables[0]) {
				tabbables[0].focus();
			} else {
				goToElement(firstNewItem);
			}
		}
	});

	if (!visibleItems?.length) {
		return null;
	}

	return (
		<div className={className}>
			<Text text={heading} as="h2" styleAs="p" className="font-bold" />
			<ul id={itemsId} ref={itemsContainerRef} className="mt-2 space-y-4">
				{visibleItems.map((campaign) => {
					const price = usePriceExcVat
						? campaign.priceExcVat
						: campaign.priceIncVat;
					return (
						<li key={campaign.id}>
							<Campaign
								id={campaign.id}
								title={campaign.title}
								campaignValidToText={campaignValidToText}
								validToDate={campaign.validTo}
								comparisonPrice={campaign.comparisonPrice}
								images={campaign.images}
								stafflingPrice={price}
								priceLabel={packagePriceLabel}
								discountType={campaign.discountType}
								onCampaignPackageAddToCartClick={
									onCampaignPackageAddToCartClick
								}
								onCampaignPackageAddToWishlistClick={
									onCampaignPackageAddToWishlistClick
								}
							/>
						</li>
					);
				})}
			</ul>
			{isCollapsable && (
				<div className="mt-2 text-center">
					<Button
						variant="text"
						onClick={() => {
							setIsExpanded((current) => !current);
						}}
						aria-controls={itemsId}
						aria-expanded={isExpanded}
					>
						{isExpanded
							? t('general_show_fewer_items_button_text')
							: t('general_show_more_items_button_text')}
					</Button>
				</div>
			)}
		</div>
	);
}
ProductDetailsCampaigns.displayName = 'ProductDetailsCampaigns';
